import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    setConsentGiven,
    showConsentBanner,
    updatePreferences
} from './cookieConsentSlice';
import {
    initializeAnalytics,
    loadGoogleAnalytics,
    initializeGTM,
    clearTrackingCookies,
    setCookie,
    getCookie,
    deleteCookie
} from './cookieManager';

const COOKIE_CONSENT_KEY = 'cookieConsent';
const CONSENT_DURATION_DAYS = 30;

export const useCookieConsent = () => {
    const dispatch = useDispatch();
    const { consentGiven, preferences, showBanner } = useSelector((state) => state.cookieConsent);

    // Load consent from cookie on mount
    useEffect(() => {
        const savedConsent = getCookie(COOKIE_CONSENT_KEY);
        if (savedConsent) {
            try {
                const consentData = JSON.parse(savedConsent);
                dispatch(setConsentGiven(consentData.consentGiven));
                dispatch(updatePreferences(consentData.preferences));
            } catch (error) {
                console.error('Error loading cookie consent:', error);
                deleteCookie(COOKIE_CONSENT_KEY);
                dispatch(showConsentBanner());
            }
        } else {
            // No cookie found = either first visit or cookie expired (30 days)
            dispatch(showConsentBanner());
        }
    }, [dispatch]);

    // Save consent to cookie whenever it changes
    useEffect(() => {
        if (consentGiven !== null) {
            const consentData = {
                consentGiven,
                preferences,
                timestamp: new Date().toISOString()
            };
            setCookie(
                COOKIE_CONSENT_KEY,
                JSON.stringify(consentData),
                CONSENT_DURATION_DAYS
            );
        }
    }, [consentGiven, preferences]);

    // Initialize tracking services based on consent
    useEffect(() => {
        if (consentGiven) {
            if (preferences.analytics) {
                loadGoogleAnalytics(consentGiven, preferences.analytics);
                initializeAnalytics(consentGiven, preferences.analytics);
            }

            if (preferences.analytics || preferences.advertisement) {
                initializeGTM(consentGiven, preferences);
            }
        } else if (consentGiven === false) {
            clearTrackingCookies();
        }
    }, [consentGiven, preferences]);

    // Handle consent withdrawal
    const handleConsentWithdrawal = () => {
        clearTrackingCookies();
        deleteCookie(COOKIE_CONSENT_KEY);
        dispatch(setConsentGiven(false));
        dispatch(updatePreferences({
            necessary: true,
            analytics: false,
            advertisement: false
        }));
        dispatch(showConsentBanner());
    };

    return {
        consentGiven,
        preferences,
        showBanner,
        handleConsentWithdrawal
    };
};
